import React from 'react';
import { Badge, Progress, Space, Tag, Tooltip, Typography, theme } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import type { Worker } from './types';
import { WorkerTelemetry, formatDuration } from '../../pages/WorkerAnalyticsPage';

const { Text } = Typography;

// One line per cell. Everything that used to sit on a second line — the city,
// the uptime, the per-day counts — is in the tooltip the cell already had, so
// a row stays one line of primary fact and the table fits the screen.

// What supervises a worker answers "who restarts it and where are its logs",
// so it belongs in the row itself rather than hidden in a tooltip.
const LAUNCHERS: Record<string, { label: string; color: string }> = {
  systemd: { label: 'systemd', color: 'green' },
  docker: { label: 'docker', color: 'blue' },
  host: { label: 'unsupervised', color: 'orange' },
};

export const uptime = (startedAt?: string): string => {
  if (!startedAt) return '';
  const ms = Date.now() - new Date(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
};

const flagOf = (country: string) =>
  String.fromCodePoint(...country.toUpperCase().split('').map((c) => 0x1F1E6 + c.charCodeAt(0) - 65));

export const NameCell: React.FC<{ worker: Worker }> = ({ worker }) => (
  <Tooltip title={`${worker.name} · ${worker.url}`}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <Text strong style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {worker.name}
      </Text>
      {worker.source === 'env' && <Tag color="blue" style={{ marginInlineEnd: 0, flexShrink: 0 }}>env</Tag>}
      {worker.source === 'discovered' && (
        <Tag color="orange" style={{ marginInlineEnd: 0, flexShrink: 0 }}>discovered</Tag>
      )}
    </span>
  </Tooltip>
);

// Country code only — the city, the public address and the ISP are one hover
// away. "Frankfurt am Main, DE" wrapped to two lines and doubled every row.
export const LocationCell: React.FC<{ worker: Worker }> = ({ worker }) => {
  if (!worker.country) return <Text type="secondary">-</Text>;
  const place = [worker.city, worker.country].filter(Boolean).join(', ');
  const tip = [place, worker.publicIp, worker.isp].filter(Boolean).join(' • ');
  return (
    <Tooltip title={tip}>
      <span style={{ whiteSpace: 'nowrap', cursor: 'help', fontSize: 12 }}>
        {flagOf(worker.country)} {worker.country.toUpperCase()}
      </span>
    </Tooltip>
  );
};

export const StatusCell: React.FC<{ worker: Worker; telemetry?: WorkerTelemetry }> = ({ worker, telemetry }) => {
  const online = !!worker.live?.online;
  const pct = telemetry?.availabilityPercent;
  const availability = pct === undefined ? null : pct < 0 ? '—' : `${pct.toFixed(1)}%`;
  const tone = pct === undefined || pct < 0 ? 'secondary' : pct >= 99.5 ? 'success' : pct >= 95 ? 'warning' : 'danger';
  return (
    <Space size={6}>
      <Badge
        status={online ? 'success' : 'error'}
        text={<Text style={{ fontSize: 12 }}>{online ? 'Online' : 'Offline'}</Text>}
      />
      {availability && telemetry && (
        <Tooltip title={`${pct !== undefined && pct < 0 ? 'Not sampled yet. ' : ''}Share of the last 7 days this worker answered${
          telemetry.disconnects ? `, ${telemetry.disconnects} drop(s), longest ${formatDuration(telemetry.longestOutageSec)}` : ''}`}>
          <Text type={tone as 'secondary' | 'success' | 'warning' | 'danger'} style={{ fontSize: 12, cursor: 'help' }}>
            {availability}
          </Text>
        </Tooltip>
      )}
    </Space>
  );
};

// Busy of concurrency. Throttled takes the bar's place when it happens: it is
// the more urgent fact and the bar only redraws numbers already in the cell.
export const LoadCell: React.FC<{ worker: Worker }> = ({ worker }) => {
  const { token } = theme.useToken();
  const live = worker.live;
  if (!live?.online) return <Text type="secondary">-</Text>;
  const pct = live.activeWorkers > 0 ? Math.round((live.busyWorkers / live.activeWorkers) * 100) : 0;
  return (
    <Tooltip title={`${live.busyWorkers} of ${live.activeWorkers} concurrent checks in flight (${pct}%)${
      live.throttled ? ' — throttled, this worker asked for fewer jobs' : ''}`}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'help' }}>
        <Text style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{live.busyWorkers} / {live.activeWorkers}</Text>
        {live.throttled
          ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>throttled</Tag>
          : <Progress
              percent={pct}
              size="small"
              showInfo={false}
              strokeColor={pct > 80 ? token.colorError : undefined}
              style={{ width: 40, marginBottom: 0, lineHeight: 1 }}
            />}
      </span>
    </Tooltip>
  );
};

// A bar per day, tallest = busiest, beside today's count on the same line.
export const ChecksCell: React.FC<{ telemetry?: WorkerTelemetry }> = ({ telemetry }) => {
  const { token } = theme.useToken();
  if (!telemetry) return <Text type="secondary">—</Text>;
  const today = telemetry.days.length ? telemetry.days[telemetry.days.length - 1].checks : 0;
  const max = Math.max(1, ...telemetry.days.map((d) => d.checks));
  return (
    <Tooltip title={
      <div style={{ fontSize: 12 }}>
        {telemetry.days.map((d) => <div key={d.day}>{d.day}: {d.checks.toLocaleString()} checks</div>)}
        <div style={{ marginTop: 4 }}>
          {telemetry.sharePercent}% of the fleet&apos;s work · {Math.round(telemetry.avgDurationMs)} ms per check
        </div>
      </div>
    }>
      <span style={{ display: 'flex', alignItems: 'flex-end', gap: 5, cursor: 'help', height: 18 }}>
        <Text style={{ fontSize: 12, lineHeight: '14px' }}>{today.toLocaleString()}</Text>
        <span style={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 14 }}>
          {telemetry.days.map((d) => (
            <span key={d.day} style={{
              width: 4,
              height: Math.max(2, Math.round((d.checks / max) * 12)),
              background: d.checks === 0 ? token.colorFillSecondary : token.colorPrimary,
            }} />
          ))}
        </span>
      </span>
    </Tooltip>
  );
};

export const BuildCell: React.FC<{ worker: Worker }> = ({ worker }) => {
  const { token } = theme.useToken();
  if (!worker.live?.version && !worker.live?.runtime) return <Text type="secondary">-</Text>;
  const rt = worker.live?.runtime;
  // The gateway keeps what a worker reported after the link drops, and that is
  // right: "what is installed there" stays true for a machine that is off.
  // "Up for N days" does not — the counter would keep climbing for a host
  // powered off last week.
  const online = !!worker.live?.online;
  const launcher = rt?.launcher ? LAUNCHERS[rt.launcher] || { label: rt.launcher, color: 'default' } : null;
  // The verdict comes from the gateway, which compares the code version the
  // worker runs (a hash of the checker's source) with the one this panel
  // installs today — the binary hash only for builds too old to report one. Only the "outdated" mark lives here; all
  // three hashes, including the one the panel once put there, are behind Info.
  const outdated = worker.software?.state === 'outdated';
  const details = rt && (
    <div style={{ fontSize: 12, lineHeight: '18px' }}>
      <div>{rt.hostname} {rt.listen} (pid {rt.pid})</div>
      {!!rt.localIps?.length && <div>addresses: {rt.localIps.join(', ')}</div>}
      <div>{rt.os}/{rt.arch}, {rt.cpus} CPUs, {rt.goVersion}</div>
      {rt.commit && <div>commit {rt.commit.slice(0, 12)}{rt.dirty ? ' (modified tree)' : ''}</div>}
      {rt.binarySha256 && <div>binary {rt.binarySha256.slice(0, 12)}…</div>}
      {rt.executablePath && <div>{rt.executablePath}</div>}
      {rt.startedAt && (online
        ? <div>up {uptime(rt.startedAt)} (since {new Date(rt.startedAt).toLocaleString()})</div>
        : <div>last started {new Date(rt.startedAt).toLocaleString()}</div>)}
      {outdated && (
        <div>
          this panel installs version {worker.software?.artifactVersion || 'a different build'} — Update will replace it
        </div>
      )}
    </div>
  );
  return (
    <Tooltip title={details} overlayStyle={{ maxWidth: 420 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0, cursor: rt ? 'help' : 'default' }}>
        <Text style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {worker.live?.version || '-'}
        </Text>
        {launcher && (
          <Tag color={launcher.color} bordered={false} style={{ marginInlineEnd: 0, fontSize: 12, flexShrink: 0 }}>
            {launcher.label}
          </Tag>
        )}
        {outdated && <WarningOutlined style={{ color: token.colorWarning, flexShrink: 0 }} />}
      </span>
    </Tooltip>
  );
};
